import { queryOptions } from '@tanstack/react-query';

import { fetchPollsGateway } from './fetchPollsGateway';
import type { PollsGateway } from '../ports/pollsGateway';

import { mySurveyResponsesKey } from '@/features/surveys/model';
import { ApiError, isApiErrorV2 } from '@/util/fetchClient';
import { userScoped } from '@/util/userScope';

/**
 * The caller's poll responses, newest first. Each row names the version it
 * answered and carries the API's results link, so a closed or revised poll
 * still lists and still opens its results.
 *
 * Keyed under the same root as every response query, so the invalidation a
 * survey submit already does refreshes this history too. The key is pinned by
 * `queryOptions.keys.test.ts`.
 */
export const getMyPollResponsesV2QueryOptions = userScoped(
  'user',
  (userId: string | undefined, gateway: PollsGateway = fetchPollsGateway) =>
    queryOptions({
      queryKey: [...mySurveyResponsesKey(userId), 'poll'],
      queryFn: () => gateway.listMyPollResponses(),
    })
);

/**
 * Results for a survey group. A client error is final, so only a server or
 * network failure is retried; retrying a 404 would just hold the spinner up
 * before the screen settles on its thank-you.
 */
export const getSurveyResultsQueryOptions = (
  surveyGroupId: string | undefined,
  gateway: PollsGateway = fetchPollsGateway
) =>
  queryOptions({
    queryKey: ['surveyResultsV2', surveyGroupId],
    queryFn: () => gateway.getSurveyResults(surveyGroupId ?? ''),
    enabled: !!surveyGroupId,
    retry: (failureCount, error) => {
      const status = error instanceof ApiError || isApiErrorV2(error) ? error.status : undefined;

      const isClientError = status !== undefined && status >= 400 && status < 500;

      return !isClientError && failureCount < 2;
    },
  });

/**
 * Whether a failed results fetch is the API withholding results rather than a
 * fault. It answers 404 alike for a non-poll, a poll the caller has not
 * answered, and an unknown survey, so a 404 only ever means "no results here".
 */
export function isSurveyResultsNotFoundError(error: unknown): boolean {
  if (!(error instanceof ApiError) && !isApiErrorV2(error)) {
    return false;
  }

  return error.status === 404;
}
